import { ClientApi } from '@quadminds/client-apis/base';
import { IUpdateOrganizationUserPayload, IUserOutputDto } from '@quadminds/shared/auth-system';
import { axiosStandardApiResolver } from '@quadminds/shared/utils';
import { API_PATHS } from './api-users-paths';
import { USERS_API_TOKEN_NAME } from './constants';
import { UsersApiConfig } from './interfaces';
import {
    CheckInviteResponse,
    CheckUserExistenceResponse,
    InviteUuidQueryParams,
    IUserSettingsOutputDto,
    IUserSelfSettingsOutputDto,
} from './types';

/**
 * Client side user API.
 */
export class UsersApi extends ClientApi {
    private accessToken: string | null = null;
    private readonly adminApiKey: string;
    private readonly adminApiKeyHeader: string;

    constructor(config?: UsersApiConfig) {
        super({
            namespace: USERS_API_TOKEN_NAME,
            ...config,
        });
        this.accessToken = config?.accessToken ?? null;
        this.adminApiKey = process.env['USERS_ADMIN_API_KEY'] ?? '';
        this.adminApiKeyHeader = process.env['USERS_ADMIN_API_KEY_HEADER'] ?? 'x-stork-admin-apikey';
    }

    public checkInvite(params: InviteUuidQueryParams) {
        return axiosStandardApiResolver<CheckInviteResponse[]>(() =>
            this.httpClient.get(API_PATHS.INVITES, { params }),
        );
    }

    /**
     * Send invite to a user by email
     */
    public sendInvite(
        organizationId: string,
        payload: {
            email: string[];
            typology: string;
            notificationType: string;
            application: string;
            roles: string[];
        },
    ) {
        return axiosStandardApiResolver(() =>
            this.httpClient.post(
                `${API_PATHS.ORGANIZATION.BASE}/${organizationId}/${API_PATHS.ORGANIZATION.INVITES}`,
                payload,
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /**
     * Get organization invites with optional filter
     */
    public getOrganizationInvites(organizationId: string, filter?: string) {
        return axiosStandardApiResolver(() =>
            this.httpClient.get(`${API_PATHS.ORGANIZATION.BASE}/${organizationId}/${API_PATHS.ORGANIZATION.INVITES}`, {
                params: filter ? { filter } : undefined,
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Delete/cancel an organization invite
     */
    public deleteInvite(organizationId: string, inviteId: string) {
        return axiosStandardApiResolver(() =>
            this.httpClient.delete(
                `${API_PATHS.ORGANIZATION.BASE}/${organizationId}/${API_PATHS.ORGANIZATION.INVITES}/${inviteId}`,
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    public checkUserExistenceByEmail(email: string) {
        return axiosStandardApiResolver<CheckUserExistenceResponse[]>(() =>
            this.httpClient.get(API_PATHS.USERS.CHECK_EMAIL, { params: { email } }),
        );
    }

    public checkUserExistenceByPhone(phone: string) {
        return axiosStandardApiResolver<CheckUserExistenceResponse[]>(() =>
            this.httpClient.get(API_PATHS.USERS.CHECK_PHONE, { params: { phone } }),
        );
    }

    public deleteUser(userId: string) {
        return axiosStandardApiResolver<CheckInviteResponse[]>(() =>
            this.httpClient.delete(API_PATHS.USERS + '/' + userId, { headers: { ...this.getAuthHeaders() } }),
        );
    }

    public updateUser(userId: string, payload: Partial<IUserOutputDto>) {
        return axiosStandardApiResolver<IUserOutputDto>(() =>
            this.httpClient.patch(`${API_PATHS.USERS.BASE}/${userId}`, payload, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public healthCheck() {
        return axiosStandardApiResolver(() => this.httpClient.get(API_PATHS.HEALTH));
    }

    // Utilities
    getAuthHeaders() {
        let authHeader = {};
        const token = this.accessToken ?? localStorage.getItem('token') ?? sessionStorage.getItem('token');
        if (token) authHeader = { Authorization: `Bearer ${token}` };
        return authHeader;
    }

    getAdminAuthHeaders() {
        return { [this.adminApiKeyHeader]: this.adminApiKey };
    }

    public updateOrganizationUserPermissions(
        organizationId: string,
        userId: string,
        permissionsToAdd: string[] = [],
        permissionsToRemove: string[] = [],
    ) {
        return axiosStandardApiResolver<IUserOutputDto[]>(() =>
            this.httpClient.patch(
                `${API_PATHS.ADMIN.ORGANIZATIONS}/${organizationId}/users/${userId}/permissions`,
                { permissionsToAdd, permissionsToRemove },
                {
                    headers: { ...this.getAdminAuthHeaders() },
                },
            ),
        );
    }

    /**
     * Get organization users with pagination and optional filters
     */
    public getOrganizationUsers(organizationId: string, page: number, limit: number, filter?: string) {
        return axiosStandardApiResolver<IUserOutputDto[]>(() =>
            this.httpClient.get(`${API_PATHS.ORGANIZATION.BASE}/${organizationId}/${API_PATHS.ORGANIZATION.USERS}`, {
                params: { page, limit, filter },
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Update organization users
     */
    public updateOrganizationUsers(organizationId: string, userId: string, payload: IUpdateOrganizationUserPayload) {
        return axiosStandardApiResolver<IUserOutputDto>(() =>
            this.httpClient.put(
                `${API_PATHS.ORGANIZATION.BASE}/${organizationId}/${API_PATHS.ORGANIZATION.USERS}/${userId}`,
                payload,
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /**
     * Delete organization user
     */
    public deleteOrganizationUser(organizationId: string, userId: string) {
        return axiosStandardApiResolver<{ id: string }>(() =>
            this.httpClient.delete(
                `${API_PATHS.ORGANIZATION.BASE}/${organizationId}/${API_PATHS.ORGANIZATION.USERS}/${userId}`,
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /*
     * Get user self settings (new endpoint)
     */
    public getUserSelfSettings(userId: string) {
        return axiosStandardApiResolver<IUserSelfSettingsOutputDto>(() =>
            this.httpClient.get(`${API_PATHS.USERS.BASE}/${userId}/self-settings`, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Update user self settings - organization specific (new endpoint)
     */
    public updateUserSelfOrganizationSettings(userId: string, settings: Record<string, unknown>) {
        return axiosStandardApiResolver<IUserSelfSettingsOutputDto>(() =>
            this.httpClient.patch(
                `${API_PATHS.USERS.BASE}/${userId}/self-settings/organization`,
                { settings },
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /**
     * Update user self settings - root level (new endpoint)
     */
    public updateUserSelfRootSettings(userId: string, settings: Record<string, unknown>) {
        return axiosStandardApiResolver<IUserSelfSettingsOutputDto>(() =>
            this.httpClient.patch(
                `${API_PATHS.USERS.BASE}/${userId}/self-settings/root`,
                { settings },
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /**
     * Create user settings
     */
    public createUserSettings(userId: string, settings: Record<string, unknown>) {
        return axiosStandardApiResolver<IUserSettingsOutputDto>(() =>
            this.httpClient.post(`${API_PATHS.USERS.BASE}/${userId}/${API_PATHS.USERS.SETTINGS}`, settings, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Update user settings
     */
    public updateUserSettings(userId: string, settings: Record<string, unknown>) {
        return axiosStandardApiResolver<IUserSettingsOutputDto>(() =>
            this.httpClient.patch(`${API_PATHS.USERS.BASE}/${userId}/${API_PATHS.USERS.SETTINGS}`, settings, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Get user settings
     */
    public getUserSettings(userId: string) {
        return axiosStandardApiResolver<IUserSettingsOutputDto>(() =>
            this.httpClient.get(`${API_PATHS.USERS.BASE}/${userId}/${API_PATHS.USERS.SETTINGS}`, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Get user settings by id
     */
    public getOrganizationUserById(organizationId: string, userId: string) {
        return axiosStandardApiResolver<IUserOutputDto>(() =>
            this.httpClient.get(
                `${API_PATHS.ORGANIZATION.BASE}/${organizationId}/${API_PATHS.ORGANIZATION.USERS}/${userId}`,
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /**
     * Update organization user settings
     */
    public updateOrganizationUserSettings(organizationId: string, userId: string, settings: Record<string, unknown>) {
        return axiosStandardApiResolver<IUserSettingsOutputDto>(() =>
            this.httpClient.patch(
                `${API_PATHS.ORGANIZATION.BASE}/${organizationId}/${API_PATHS.ORGANIZATION.USERS}/${userId}/${API_PATHS.USERS.SETTINGS}`,
                settings,
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    /**
     *get roles
     */
    public getRoles() {
        return axiosStandardApiResolver<{
            id: string;
            name: string;
            permissions: { id: string; name: string; code: string }[];
        }>(() =>
            this.httpClient.get(`${API_PATHS.ADMIN.ROLES}`, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     *get roles
     */
    public getApplications() {
        return axiosStandardApiResolver<
            {
                id: string;
                name: string;
            }[]
        >(() =>
            this.httpClient.get(`${API_PATHS.ADMIN.APPLICATIONS}`, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }
}
